/**
 * Fix an entry that is already logged — by re-weighing it.
 *
 * Entries snapshot their macros so that editing a *food* never rewrites
 * history — that rule protects last Tuesday from an unrelated edit, and it
 * stands. This is the opposite case: someone is looking at one entry they know
 * is wrong. Until now the only way to fix it was to delete and re-log, which
 * throws away the time it was eaten.
 *
 * **The portion is the only thing this edits.** Opening a row used to present
 * five fields — weight, calories, protein, carbs, fat — and four of them were
 * the wrong answer to what had actually happened. Nearly every correction made
 * on this screen is "that was 180 g, not 100": the food is right, the numbers
 * per 100 g are right, and only the amount on the plate was guessed. Typing new
 * macros for one entry fixed the day and left the food still wrong for the next
 * time it is logged, so the more someone used those fields the more the same
 * correction had to be made again. So the weight is here, and the figures are
 * where they came from: `Fix the product` under this editor edits the food, for
 * this entry and every future one.
 *
 * Only what changed is sent, and a weight alone is all the server needs — it
 * rescales from the food the entry came from, or from the entry's own snapshot
 * when that food is gone. The preview over the pad runs the same arithmetic on
 * whichever of those two the server will use, so the numbers shown before
 * saving are the numbers saved.
 *
 * The exception is an entry with no weight: calories entered by hand have no
 * food behind them and no portion to scale, so nothing but the figures can be
 * corrected, and those get the fields they always had.
 *
 * **The weight is typed on the app's own pad, and the pad's key saves.** A
 * number field here raised the system keyboard over the bottom half of the day,
 * which is where the meal being corrected usually is — you lost sight of the
 * row you had opened, and of what the three rows around it came to. It is the
 * same weight the sheet asks for when a food is first logged, so it is asked
 * the same way: `GramsPad`, portions one tap away, and `Save` inside the keys
 * rather than a button below them that a keyboard would have covered anyway.
 */

import {
  forGrams,
  parseDecimal,
  suggestedPortions,
  useFood,
  useFoodPortions,
  useUpdateLog,
  type FoodLog,
  type Macros,
} from "@calorio/core";
import { useState } from "react";

import { GramsPad } from "./GramsPad";
import { useCalorio } from "../app/providers";

const FIELDS = [
  ["kcal", "Calories"],
  ["protein_g", "Protein"],
  ["carbs_g", "Carbs"],
  ["fat_g", "Fat"],
] as const;

type Field = (typeof FIELDS)[number][0];

const round = (n: number) => Math.round(n * 10) / 10;

const snapshot = (entry: FoodLog): Macros => ({
  kcal: entry.kcal,
  protein_g: entry.protein_g,
  carbs_g: entry.carbs_g,
  fat_g: entry.fat_g,
});

export function EntryEditor({ entry, onDone }: { entry: FoodLog; onDone: () => void }) {
  const ctx = useCalorio();
  const update = useUpdateLog(ctx);

  // Quick-added calories have no weight and no food. Nothing to re-weigh, so
  // the figures stay editable for those and only those.
  const weighed = entry.quantity_g > 0;

  // Shares its cache with the "Fix the product" panel that opens right under
  // this one, so opening an editor is usually not a request at all.
  const food = useFood(ctx, weighed ? entry.food_item_id : null);
  // The weights this food is usually logged at, for the chips. A correction is
  // most often "the usual amount, not the guess", and the usual amount is here.
  const habits = useFoodPortions(ctx, weighed ? entry.food_item_id : null);

  const [grams, setGrams] = useState(weighed ? String(round(entry.quantity_g)) : "");
  const [values, setValues] = useState<Record<Field, string>>({
    kcal: String(round(entry.kcal)),
    protein_g: String(round(entry.protein_g)),
    carbs_g: String(round(entry.carbs_g)),
    fat_g: String(round(entry.fat_g)),
  });
  const [problem, setProblem] = useState<string | null>(null);

  const unit = food.data?.basis_unit ?? "g";

  /**
   * What one unit of this entry is worth, as the server would count it.
   *
   * From the food when it still exists and from the entry's frozen snapshot
   * when it doesn't — the same two sources, in the same order, that the server
   * picks between. A figure derived the other way would disagree with the row
   * it replaces whenever the food had been corrected since it was logged.
   *
   * The snapshot is per-portion rather than per-100, so it is divided back by
   * the weight it was taken at. Both the pad's running kcal and the preview
   * under it scale from this one number, which is why they can never disagree.
   */
  const per100: Macros = food.data
    ? {
        kcal: food.data.kcal_per_100,
        protein_g: food.data.protein_g_per_100,
        carbs_g: food.data.carbs_g_per_100,
        fat_g: food.data.fat_g_per_100,
      }
    : forGrams(snapshot(entry), (100 / entry.quantity_g) * 100);

  /** What the day will read once this is saved. */
  const preview = (): Macros | null => {
    const weight = parseDecimal(grams);
    if (!Number.isFinite(weight) || weight <= 0) return null;
    return forGrams(per100, weight);
  };

  const save = async () => {
    setProblem(null);
    const changes: Partial<FoodLog> = {};

    if (weighed) {
      const weight = parseDecimal(grams);
      if (!grams.trim() || !Number.isFinite(weight) || weight <= 0) {
        return setProblem("That needs to be a weight.");
      }
      if (round(weight) !== round(entry.quantity_g)) changes.quantity_g = weight;
    } else {
      for (const [field] of FIELDS) {
        const value = parseDecimal(values[field]);
        if (!Number.isFinite(value) || value < 0) {
          return setProblem("Those need to be numbers.");
        }
        if (round(value) !== round(entry[field])) changes[field] = value;
      }
    }

    // Nothing touched — closing is the whole of the correct behaviour.
    if (!Object.keys(changes).length) return onDone();

    try {
      await update.mutateAsync({ id: entry.id, changes });
      onDone();
    } catch (error) {
      setProblem(error instanceof Error ? error.message : "Couldn't save that.");
    }
  };

  const after = weighed ? preview() : null;

  return (
    <div className="entry-edit stack" style={{ gap: "var(--sp-2)" }}>
      {weighed ? (
        <>
          {/* The figures, as a reading rather than as fields. They are what the
              weight produces, so they are shown and not asked for — and
              announced, because the only thing that moved is a number nobody
              typed. Above the pad rather than below it: the keys are where the
              thumb is, so anything under them is off the bottom of what the
              eye is looking at.

              Named `Saves as`, because the row it will replace is now sitting
              directly above it saying almost the same words. Two identical
              lines is a bug report; a labelled one is the before and after. */}
          <p className="entry-edit__after" aria-live="polite">
            <span className="entry-edit__after-label">Saves as</span>{" "}
            <span className="num">
              {after ? (
                <>
                  {Math.round(after.kcal)} kcal · P {Math.round(after.protein_g)} · C{" "}
                  {Math.round(after.carbs_g)} · F {Math.round(after.fat_g)}
                </>
              ) : (
                "—"
              )}
            </span>
          </p>

          <p className="muted" style={{ margin: 0, fontSize: "var(--step--1)" }}>
            The figures follow the weight. If they are wrong at every weight, fix the food below.
          </p>

          {/* `Save` and not `Add`, because this weight replaces one rather than
              writing a new row — and disabled while the write is in flight, so
              the same key cannot send it twice. */}
          <GramsPad
            name={entry.name}
            value={grams}
            onChange={setGrams}
            unit={unit}
            per100={per100}
            chips={food.data ? suggestedPortions(food.data, habits.data) : undefined}
            action={update.isPending ? "Saving…" : "Save"}
            disabled={update.isPending}
            onCommit={save}
          />
        </>
      ) : (
        <>
          {FIELDS.map(([field, label]) => (
            <label className="manual__row" key={field}>
              <span>{label}</span>
              <input
                className="field num manual__value"
                inputMode="decimal"
                value={values[field]}
                onChange={(e) => setValues((v) => ({ ...v, [field]: e.target.value }))}
                aria-label={`${label} of ${entry.name}`}
              />
            </label>
          ))}

          <p className="muted" style={{ margin: 0, fontSize: "var(--step--1)" }}>
            Entered as calories, with no weight behind it — so the figures are the only thing to
            correct.
          </p>
        </>
      )}

      {problem && (
        <p className="error" style={{ margin: 0 }}>
          {problem}
        </p>
      )}

      {/* Weighed rows save on the pad's own key, so all that is left down here
          is the way out. The figures branch keeps its button: it is a form with
          four fields and no pad, and the system keyboard is the right keyboard
          for it. */}
      {weighed ? (
        <button type="button" className="signin__secondary entry-edit__cancel" onClick={onDone}>
          Cancel
        </button>
      ) : (
        <div style={{ display: "flex", gap: "var(--sp-2)" }}>
          <button className="button" onClick={save} disabled={update.isPending} style={{ flex: 1 }}>
            {update.isPending ? "Saving…" : "Save"}
          </button>
          <button type="button" className="signin__secondary" onClick={onDone}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
